import { buildErrorResponse, buildResultResponse } from './JsonRpcResponse';
import { unexpectedError, toModelingError } from './MCPModelingError';
import type { ExecuteModelingFailure } from './MCPModelingError';
import { captureFailureFromError, toCaptureError } from './MCPCaptureError';
import type { ExecuteCaptureFailure } from './MCPCaptureError';
import { MCPBridgeOperationLocator } from './MCPBridgeOperationLocator';
import { ViewImageCaptureLocator } from '../../ViewImageCapture/ViewImageCaptureLocator';
import type { ViewImageCaptureAPI } from '../../ViewImageCapture/ViewImageCaptureLocator';
import { BridgeChatNotReadyError } from './MCPBridgeChatLocator';
import { BridgeBusyError } from './MCPBridgeChatSession';
import type { MCPBridgeSpeech } from './RequestModelingResponse';
import type { CaptureImageResult } from '../../ViewImageCapture/CaptureImageResult';
import { methodNotFoundError } from './JsonRpcError';
import type { JsonRpcError } from './JsonRpcError';
import { JsonRpcRequest } from './JsonRpcRequest';
import { RequestModelingRequest } from './RequestModelingRequest';
import { GetViewModelImageRequest } from './GetViewModelImageRequest';
import { GetViewImageRequest } from './GetViewImageRequest';
import type { MCPBridgeLoggerLike } from './MCPBridgeLogger';
import { toSafeString } from './logString';

type ExecuteModelingResult = { ok: true; completedAt: string; messages: MCPBridgeSpeech[] } | ExecuteModelingFailure;

type ExecuteCaptureResult = { ok: true; value: CaptureImageResult } | ExecuteCaptureFailure;

// decide() から handle() へ返す構造化結果。handle() 側でのみ「実際に送信した」ログを出すため、
// 送信対象 (response) と、送信成功時に出すログのメタ情報 (id / errorCode) を分けて持たせる。
// noop は Notification で「返信すべき文字列がない」ケース。parse 失敗は常に error outcome を返す。
type DecideOutcome =
  | { kind: 'noop' }
  | { kind: 'result'; response: string; id: string }
  | { kind: 'error'; response: string; id: string | null; errorCode: number };

// handle() が呼び出し側に要求する send コールバックの戻り値。
// 単なる boolean で「送れたか」を返させると、drop パスで true を返す、または
// 送信成功後に return を書き忘れるといった convention 違反が silent に通る。
// 判別型 + reason 必須にすることで、drop 時は必ず reason を積む形へ型で誘導し、
// Sent ログの真実性を「呼び出し側の規律」ではなく型契約で担保する。
export type SendResult = { sent: true } | { sent: false; reason: string };

// JSON-RPC id は仕様上「任意の string」を受け付けるため、非 BMP 文字が含まれる可能性がある。
// slice(0, 8) は UTF-16 code unit 単位で切るためサロゲートペアを途中で割り得るので、
// Array.from でコードポイント配列に落としてから先頭 8 個を取り出す。
function shortenId(id: string): string {
  return Array.from(id).slice(0, 8).join('');
}

// "Sent error (id: X, code: Y)" のような組み立てで、id 省略時は "Sent error (code: Y)" にする。
// idPart の末尾に ", " を埋めるパターンは区切り文字を条件分岐に染み出させ、フィールド追加時の
// 事故を招くので、parts 配列 + join で統一する。
function formatFieldsList(fields: Array<[key: string, value: string | number] | null>): string {
  return fields
    .filter((f): f is [string, string | number] => f !== null)
    .map(([k, v]) => `${k}: ${v}`)
    .join(', ');
}

export class MCPBridgeMessageHandler {
  constructor(private readonly logger: MCPBridgeLoggerLike) {}

  // send は SendResult を返す契約。drop 経路（socket 差替・非 OPEN・同期例外など）は
  // { sent: false, reason } を返さないと型が通らないため、drop 時に reason を積み忘れる or
  // 送信していないのに sent: true を返すといった convention 違反が構造的に防げる。
  // Sent ログの真実性は呼び出し側の規律ではなく型契約で担保される。
  async handle(event: MessageEvent, send: (data: string) => SendResult): Promise<void> {
    const outcome = await this.decide(event);
    if (outcome.kind === 'noop') return;

    const result = send(outcome.response);
    if (!result.sent) return;

    if (outcome.kind === 'result') {
      this.logger.info(`Sent result (id: ${shortenId(outcome.id)})`);
    } else {
      this.logger.info(this.formatSentErrorLog(outcome.id, outcome.errorCode));
    }
  }

  async decide(event: MessageEvent): Promise<DecideOutcome> {
    let responseId: string | null = null;
    try {
      const parsed = JsonRpcRequest.parse(event.data);

      // パース失敗: id が確定していればその id で、未確定なら id:null でエラーレスポンス
      if (!parsed.ok) {
        this.logger.info(this.formatReceivedInvalidLog(parsed.id));
        return this.makeErrorOutcome(parsed.id, parsed.error);
      }

      // 有効な Notification: 本プロジェクトでは受け付けない方針。
      // JSON-RPC 仕様上 Notification へのレスポンスは禁止のため send を抑止して終了。
      // ログ文言は silent-drop の挙動と一致させるため「Ignored」で始める。
      if (parsed.kind === 'notification') {
        this.logger.info(`Ignored notification: ${parsed.value.method}`);
        return { kind: 'noop' };
      }

      // ここから先は kind === 'request'（id 付きリクエスト）。
      // ここでの "Received" は「メッセージが到着した」ことのみを意味し、実行の受理を含まない
      // （後段の param 検証で拒否される可能性がある）。実行の成否は "Sent result/error" 側で示す。
      responseId = parsed.value.id;
      this.logger.info(`Received ${parsed.value.method} (id: ${shortenId(responseId)})`);

      return await this.dispatch(parsed.value);
    } catch (e) {
      const detail = e instanceof Error ? e.message : toSafeString(e);
      return this.makeErrorOutcome(responseId, unexpectedError(detail));
    }
  }

  // method 名 → ハンドラのディスパッチ。MCP 経由で実行できる操作をここで明示的に列挙する。
  // 未知メソッドは従来通り -32601 Method not found を返す。
  private async dispatch(req: JsonRpcRequest): Promise<DecideOutcome> {
    switch (req.method) {
      case RequestModelingRequest.METHOD:
        return await this.decideRequestModeling(req);
      case GetViewModelImageRequest.METHOD:
        return await this.decideGetViewModelImage(req);
      case GetViewImageRequest.METHOD:
        return await this.decideGetViewImage(req);
      default:
        return this.makeErrorOutcome(req.id, methodNotFoundError('unknown_method', `unknown method: ${req.method}`));
    }
  }

  private async decideRequestModeling(req: JsonRpcRequest): Promise<DecideOutcome> {
    const reqResult = RequestModelingRequest.from(req);
    if (!reqResult.ok) {
      return this.makeErrorOutcome(req.id, reqResult.error);
    }

    const result = await this.executeModeling(reqResult.value);
    if (!result.ok) {
      return this.makeErrorOutcome(req.id, toModelingError(result));
    }

    const response = buildResultResponse(req.id, {
      completedAt: result.completedAt,
      messages: result.messages,
    });
    return { kind: 'result', response, id: req.id };
  }

  private async decideGetViewModelImage(req: JsonRpcRequest): Promise<DecideOutcome> {
    const reqResult = GetViewModelImageRequest.from(req);
    if (!reqResult.ok) {
      return this.makeErrorOutcome(req.id, reqResult.error);
    }

    return await this.decideCapture(req.id, (api) => api.captureViewModelImage());
  }

  private async decideGetViewImage(req: JsonRpcRequest): Promise<DecideOutcome> {
    const reqResult = GetViewImageRequest.from(req);
    if (!reqResult.ok) {
      return this.makeErrorOutcome(req.id, reqResult.error);
    }

    const target = reqResult.value.target;
    return await this.decideCapture(req.id, async (api) => (await api.captureViewImage(target)).image);
  }

  private async decideCapture(
    id: string,
    run: (api: ViewImageCaptureAPI) => Promise<CaptureImageResult>
  ): Promise<DecideOutcome> {
    const result = await this.executeCapture(run);
    if (!result.ok) {
      return this.makeErrorOutcome(id, toCaptureError(result));
    }

    // 成功 result はワイヤ仕様（decisions.md §2）の CaptureImageResult をそのまま載せる
    return { kind: 'result', response: buildResultResponse(id, result.value), id };
  }

  private makeErrorOutcome(id: string | null, error: JsonRpcError): DecideOutcome {
    return { kind: 'error', response: buildErrorResponse(id, error), id, errorCode: error.code };
  }

  private formatReceivedInvalidLog(id: string | null): string {
    // parse 失敗でも id が確定していれば載せる。後段の "Sent error (id: X, ...)" と相関できる。
    if (id === null) return 'Received invalid message';
    return `Received invalid message (id: ${shortenId(id)})`;
  }

  private formatSentErrorLog(id: string | null, errorCode: number): string {
    const fields = formatFieldsList([id === null ? null : ['id', shortenId(id)], ['code', errorCode]]);
    return `Sent error (${fields})`;
  }

  private async executeModeling(req: RequestModelingRequest): Promise<ExecuteModelingResult> {
    // モデリング API 実行前にユーザー発話をログへ残す。
    // これにより not_ready / busy / execution_failed で失敗した場合でも発話自体は記録される。
    this.logger.userSpeech(req.instruction);

    const api = MCPBridgeOperationLocator.get();
    if (api === null) {
      return { ok: false, kind: 'not_ready' };
    }
    try {
      const { completedAt, messages } = await api.requestModeling(req.viewModelId, req.instruction);
      return { ok: true, completedAt, messages };
    } catch (e) {
      if (e instanceof BridgeChatNotReadyError) {
        return { ok: false, kind: 'not_ready' };
      }
      if (e instanceof BridgeBusyError) {
        return { ok: false, kind: 'busy' };
      }
      const detail = e instanceof Error ? e.message : String(e);
      return { ok: false, kind: 'execution_failed', detail };
    }
  }

  private async executeCapture(
    run: (api: ViewImageCaptureAPI) => Promise<CaptureImageResult>
  ): Promise<ExecuteCaptureResult> {
    const api = ViewImageCaptureLocator.get();
    if (api === null) {
      return { ok: false, kind: 'not_ready' };
    }
    try {
      return { ok: true, value: await run(api) };
    } catch (e) {
      return captureFailureFromError(e);
    }
  }
}
